import User from "../../../../models/User";

/**
 * Resume formats the server accepts (keep in sync with
 * TalentResumeValidator::ContentTypes). The server checks the file's
 * contents; this list only filters the file picker.
 */
export const RESUME_EXTENSIONS = [
    'pdf',
    'doc', 'docx',
    'pptx',
    'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff',
];

export const RESUME_ACCEPT = RESUME_EXTENSIONS.map(extension => `.${extension}`).join(',');

export const RESUME_MAX_SIZE_MB = 10;

export const RESUME_FORMATS_LABEL = 'PDF, Word, PowerPoint, or an image (JPG, PNG)';

/**
 * Where a talent's resume is used, shown wherever we explain the requirement.
 */
export const RESUME_USES = [
    {
        key: 'role-scores',
        title: 'Role Resume Scores',
        description: 'How closely your experience matches each role you add to your profile.',
    },
    {
        key: 'hire-confidence',
        title: 'Hire Confidence Score',
        description: 'A signal clients use to gauge your fit when reviewing candidates.',
    },
    {
        key: 'ai-scoring',
        title: 'AI Resume Scoring',
        description: 'How we match you to projects and put you in front of the right clients.',
    },
];

const SAVE_AS_PDF_TIP = 'Export or "Save as PDF" from Word, Google Docs, Pages or Canva. Text-based PDFs work best.';

/**
 * What we tell the talent for each failure reason returned by the server
 * (TalentResumeValidator::Reason*) or detected in the browser.
 */
export const FAILURE_REASONS = {
    unsupported_type: {
        title: "We can't read that type of file",
        summary: `Upload your resume as a ${RESUME_FORMATS_LABEL}.`,
        tips: [
            SAVE_AS_PDF_TIP,
            'Pages, Keynote, older PowerPoint (.ppt) and OpenDocument files need to be exported as a PDF first.',
        ],
    },
    too_large: {
        title: 'That file is too large',
        summary: `Resumes need to be ${RESUME_MAX_SIZE_MB} MB or smaller.`,
        tips: [
            'Export a PDF with "Reduce file size" or "Minimum size" turned on.',
            'Remove large images or embedded portfolio pieces. Share those in your Work Samples instead.',
        ],
    },
    unreadable: {
        title: "We couldn't read any text in this file",
        summary: 'We need to be able to read the words on your resume to score it.',
        tips: [
            'If the file is password protected, remove the password and upload it again.',
            'Open the file to make sure it isn\'t damaged, then save a fresh copy.',
            'Design tools like Canva, Figma or Illustrator can turn text into shapes. Export with text kept as text, or Save as PDF from a word processor.',
            'For a scan or photo, use a clear, straight, well-lit image of every page.',
        ],
    },
    too_little_text: {
        title: 'We only found a little text',
        summary: "This file doesn't seem to contain your full resume.",
        tips: [
            'Make sure you uploaded your complete resume and not just a cover page or a single graphic.',
            'If your resume is mostly visual, upload a text-based version that lists your roles, clients and skills.',
            'For a scan or photo, include every page and make sure the text is in focus.',
        ],
    },
    not_a_resume: {
        title: "This doesn't look like a resume",
        summary: 'Upload a resume that lists your work history, such as roles, employers or clients, and your skills.',
        tips: [
            'Cover letters, portfolios, certificates and job descriptions can\'t be used on their own.',
            'A LinkedIn profile saved as a PDF works well if you don\'t have a resume handy.',
        ],
    },
    upload_failed: {
        title: "Your file didn't upload",
        summary: 'Something interrupted the upload before we could check your resume.',
        tips: [
            'Check your internet connection and try again.',
            `Make sure the file is ${RESUME_MAX_SIZE_MB} MB or smaller.`,
        ],
        canRetry: true,
    },
    timeout: {
        title: 'Checking your resume took too long',
        summary: 'This can happen with long scanned documents or a slow connection.',
        tips: [
            'Try again. It often works on the second attempt.',
            'If it keeps happening, upload a text-based PDF instead of a scan or image.',
        ],
        canRetry: true,
    },
};

export class ResumeVerificationError extends Error {
    /**
     * @param {String} reason Key of FAILURE_REASONS
     * @param {String|null} detail Server or AI provided explanation
     * @param {String|null} documentType What the file looked like, e.g. "cover letter"
     */
    constructor(reason, detail = null, documentType = null) {
        super(detail || reason);
        this.name = 'ResumeVerificationError';
        this.reason = FAILURE_REASONS[reason] ? reason : 'upload_failed';
        this.detail = detail;
        this.documentType = documentType;
    }

    get feedback() {
        return FAILURE_REASONS[this.reason];
    }

    get canRetry() {
        return !!this.feedback.canRetry;
    }
}

export const fileExtension = (fileName = '') => {
    const parts = String(fileName).toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
};

/**
 * Catch problems we can spot before uploading.
 *
 * @param {File} file
 * @returns {ResumeVerificationError|null}
 */
export const checkResumeFileLocally = (file) => {
    if (!RESUME_EXTENSIONS.includes(fileExtension(file?.name))) {
        return new ResumeVerificationError('unsupported_type');
    }
    if (file.size > RESUME_MAX_SIZE_MB * 1024 * 1024) {
        return new ResumeVerificationError('too_large');
    }
    return null;
};

/**
 * Upload a resume and have the server confirm it's readable. The talent's
 * current resume is only replaced when the check passes.
 *
 * @param {File} file
 * @returns {Promise<User>} The updated auth user
 * @throws {ResumeVerificationError}
 */
export const uploadAndVerifyResume = async (file) => {
    const localError = checkResumeFileLocally(file);
    if (localError) {
        throw localError;
    }

    const request = TB.request(
        TB.api_endpoint('/api/sessions/resume'), { resume: file }
    ).headers({
        'Content-Type': 'multipart/form-data'
    });

    let response;
    try {
        // OCR on scans and images can run past the default request timeout
        response = await request.post({ timeout: 200 * 1000 });
    } catch (error) {
        const status = error?.response?.status;
        const failure = error?.response?.data?.error?.data;

        if (failure?.reason) {
            throw new ResumeVerificationError(failure.reason, failure.message, failure.document_type);
        }
        if (status === 413) {
            throw new ResumeVerificationError('too_large');
        }
        throw new ResumeVerificationError(error?.response ? 'upload_failed' : 'timeout');
    }

    const user = User.make(response.success.data.auth);
    await TB.auth().update(user);

    return user;
};
